import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, fileUrl } from "@/lib/quotationApi";
import { useAuth } from "@/lib/auth";

const DEFAULTS = {
  firm_name: "Your Studio",
  tagline: "Interior Quotations",
  logo_storage_path: "",
  primary_color: "#C5A880",
  gst_number: "",
  address: "",
  show_business_info: false,
  bank_account_name: "",
  bank_account_number: "",
  bank_name: "",
  ifsc_code: "",
  upi_id: "",
  qr_storage_path: "",
  show_payment_info: false,
  validity_days: 15,
  footer_note: "",
};

// Re-exported for public (unauthenticated) pages that fetch branding by workspace_id.
export const PUBLIC_DEFAULT_BRANDING = { ...DEFAULTS };

const BrandingContext = createContext({
  branding: DEFAULTS,
  logoUrl: "",
  refresh: () => {},
  setBranding: () => {},
});

// Adjusts a hex color's HSL lightness by `delta` (e.g. +10 lighter, -10 darker).
function shiftLightness(hex, delta) {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h.padEnd(6, "0").slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      default: hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  const nl = Math.min(1, Math.max(0, l + delta / 100));
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = nl < 0.5 ? nl * (1 + s) : nl + s - nl * s;
  const p = 2 * nl - q;
  const nr = Math.round(hue2rgb(p, q, hh + 1 / 3) * 255);
  const ng = Math.round(hue2rgb(p, q, hh) * 255);
  const nb = Math.round(hue2rgb(p, q, hh - 1 / 3) * 255);
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`.toUpperCase();
}

export function applyBrandingCSS(primaryColor) {
  const root = document.documentElement;
  if (!primaryColor) return;
  root.style.setProperty("--primary", primaryColor);
  root.style.setProperty("--primary-hover", shiftLightness(primaryColor, 10));
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);
  const auth = useAuth();
  const activeWs = auth?.user?.active_workspace_id || null;

  const load = useCallback(async () => {
    if (!activeWs) {
      setBranding(DEFAULTS);
      applyBrandingCSS(DEFAULTS.primary_color);
      return;
    }
    try {
      const r = await api.get("/settings/branding");
      const merged = { ...DEFAULTS, ...r.data };
      setBranding(merged);
      applyBrandingCSS(merged.primary_color);
    } catch {
      applyBrandingCSS(DEFAULTS.primary_color);
    }
  }, [activeWs]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const name = (branding.firm_name || "Interiors Quote Maker").trim();
    const tag = (branding.tagline || "").trim();
    document.title = activeWs && name ? (tag ? `${name} · ${tag}` : name) : "Interiors Quote Maker";
  }, [branding.firm_name, branding.tagline, activeWs]);

  const logoUrl = branding.logo_storage_path ? fileUrl(branding.logo_storage_path) : "";

  // Memoise the context value so consumers don't re-render every time the
  // BrandingProvider re-renders with the same logical state.
  const ctxValue = useMemo(
    () => ({ branding, logoUrl, refresh: load, setBranding }),
    [branding, logoUrl, load],
  );

  return (
    <BrandingContext.Provider value={ctxValue}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

// Convert a hex string into [r, g, b] (for jsPDF colour setters).
export function hexToRgb(hex) {
  const h = (hex || "").replace("#", "");
  if (!h) return [197, 168, 128];
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h.padEnd(6, "0").slice(0, 6);
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}
